// frontend/src/features/livestock/compareZoom.ts
// One zoom for both shots in compare (docs/plans/2026-10-29-tank-shot-framing-design.md, 9):
// a scale from 1 to 4 and the point of the frame (fractions, 0..1) at the view's
// top left. Pure functions; CompareSlider and CompareSideBySide draw it with
// zoomTransform, so the two shots can't drift apart. The photo viewer
// (PhotoLightbox) uses it too, with its own maximum and the letterboxed
// photo's box as the limits.

import type { CSSProperties } from 'react';

export interface CompareZoom {
  scale: number;
  /** The frame point at the view's left edge, as a fraction of the frame's width. */
  left: number;
  /** The frame point at the view's top edge, as a fraction of the frame's height. */
  top: number;
}

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
/** One press of a zoom button or key. */
export const ZOOM_STEP = 1.5;
export const NO_ZOOM: CompareZoom = { scale: 1, left: 0, top: 0 };

/** Where the picture is in the frame, as fractions of the frame (0..1). */
export interface ZoomContent {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ZoomLimits {
  /** The largest scale; ZOOM_MAX when not given. */
  max?: number;
  /**
   * The picture's box when it doesn't fill the frame (a letterboxed photo):
   * the view stays on it, and centred on it along a side shorter than the view.
   * The whole frame when not given.
   */
  content?: ZoomContent;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** The view's start along one side: on the content, or centred on it when it's shorter. */
function clampSide(start: number, span: number, from: number, to: number): number {
  const placed = to - from >= span ? clamp(start, from, to - span) : (from + to) / 2 - span / 2;
  return clamp(placed, 0, 1 - span);
}

/** Keeps the scale in range and the view inside the frame (or the content). */
export function clampZoom(zoom: CompareZoom, limits: ZoomLimits = {}): CompareZoom {
  const scale = clamp(zoom.scale, ZOOM_MIN, limits.max ?? ZOOM_MAX);
  const span = 1 / scale;
  const content = limits.content ?? { left: 0, top: 0, right: 1, bottom: 1 };
  return {
    scale,
    left: clampSide(zoom.left, span, content.left, content.right),
    top: clampSide(zoom.top, span, content.top, content.bottom),
  };
}

/** Zoom by `factor` keeping the view point (u, v) (fractions of the view) still. */
export function zoomAt(
  zoom: CompareZoom,
  factor: number,
  u: number,
  v: number,
  limits: ZoomLimits = {}
): CompareZoom {
  const x = zoom.left + u / zoom.scale;
  const y = zoom.top + v / zoom.scale;
  const scale = clamp(zoom.scale * factor, ZOOM_MIN, limits.max ?? ZOOM_MAX);
  return clampZoom({ scale, left: x - u / scale, top: y - v / scale }, limits);
}

/** Drag the view by (du, dv), fractions of the view's size: the picture follows the pointer. */
export function panZoom(
  zoom: CompareZoom,
  du: number,
  dv: number,
  limits: ZoomLimits = {}
): CompareZoom {
  return clampZoom(
    {
      ...zoom,
      left: zoom.left - du / zoom.scale,
      top: zoom.top - dv / zoom.scale,
    },
    limits
  );
}

export function isZoomed(zoom: CompareZoom): boolean {
  return zoom.scale > ZOOM_MIN;
}

/** The style for the layer holding both shots (transform-origin 0 0). */
export function zoomTransform(zoom: CompareZoom): CSSProperties | undefined {
  if (!isZoomed(zoom)) return undefined;
  return {
    transformOrigin: '0 0',
    transform: `scale(${zoom.scale}) translate(${-zoom.left * 100}%, ${-zoom.top * 100}%)`,
  };
}

/** "1x", "1.5x", "2x": the reset button's label. */
export function zoomLabel(zoom: CompareZoom): string {
  return `${Number(zoom.scale.toFixed(1))}x`;
}
